// =============================================================
// VARIANCE — The Executive Command Center
// File      : ProjectDetail.tsx
// Location  : /home/Applications/variance/web/src/pages/ProjectDetail.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Premium Project Command Center with Finance Tab.
//             Part 1: Imports, interfaces, state, all handlers,
//             and TABs 1-3 (Milestones, Inventory, Additional Works).
//             Part 2 (separate) delivers TAB 4 (Finance) + all
//             slide-outs + closing tags.
// =============================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, IndianRupee, PieChart, AlertTriangle, Edit, Trash2, Banknote, Building, FileText, TrendingUp, ChevronRight, CheckCircle, Clock } from 'lucide-react';
import { PageHelp, HelpTooltip } from '../components/HelpSystem';
import ReactECharts from 'echarts-for-react';

interface Project {
  id: string; name: string; code: string; location: string | null;
  company_name: string; client_name: string | null; jv_partners: string | null;
}
interface Milestone {
  id: string; milestone_type_id: string; milestone_type: string; milestone_name: string;
  category: string | null; section: string | null;
  planned_amount: number | null; actual_amount: number | null;
  weightage_pct: number; physical_progress_pct: number;
  planned_date: string | null; actual_date: string | null; status: string;
  cluster_name?: string | null; unit_number?: string | null;
}
interface Inventory {
  id: string; cluster_name: string; unit_number: string;
  sale_area_sqft: number | null; builtup_area_sqft: number | null;
  sales_status: string; construction_status: string;
}
interface MilestoneType {
  id: string; type_name: string; project_id: string;
}
interface AdditionalWork {
  id: string; project_id: string; cluster_name: string; unit_number: string;
  item_name: string; description: string; amount: number;
  gst_pct: number; cess_pct: number; notes: string;
}
interface Budget {
  id: string; budget_head: string; original_amount: number;
  total_revised: number; current_budget: number;
}
interface BudgetRevision {
  id: string; budget_id: string; revision_amount: number; reason: string;
  created_at?: string;
}
interface Invoice {
  id: string; vendor_name: string; invoice_number: string;
  invoice_date: string; amount: number; tax_amount: number; total_amount: number;
  status: string; budget_head: string;
}
interface Vendor {
  id: string; name: string; vendor_type: string;
}

export function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<Project | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [milestoneTypes, setMilestoneTypes] = useState<MilestoneType[]>([]);
  const [allProjects, setAllProjects] = useState<{id: string, name: string}[]>([]);
  const [clusters, setClusters] = useState<string[]>([]);
  const [additionalWorks, setAdditionalWorks] = useState<AdditionalWork[]>([]);
  
  // Finance State
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  
  // Vendor Dropdown State
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('All');
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<'milestones' | 'inventory' | 'additional-works' | 'finance'>('milestones');
  
  // Modals State (RESTORED)
  const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isClusterModalOpen, setIsClusterModalOpen] = useState(false);
  const [isWorkModalOpen, setIsWorkModalOpen] = useState(false);
  
  // Finance Modals
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [isRevisionModalOpen, setIsRevisionModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);

  // Form States
  const [newTypeName, setNewTypeName] = useState('');
  const [newClusterName, setNewClusterName] = useState('');
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [editingWorkId, setEditingWorkId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState(id || '');
  
  const [milestoneData, setMilestoneData] = useState({
    milestone_type_id: '', milestone_name: '', category: '', section: '',
    planned_amount: '', weightage_pct: '', planned_date: '', 
    cluster_name: '', unit_number: ''
  });
  const [inventoryData, setInventoryData] = useState({
    cluster_name: '', unit_number: '', sale_area_sqft: '', builtup_area_sqft: ''
  });
  const [workData, setWorkData] = useState({ 
    item_name: '', description: '', amount: '', gst_pct: '18', cess_pct: '0', 
    cluster_name: '', unit_number: '', notes: '' 
  });
  const [budgetData, setBudgetData] = useState({ budget_head: '', original_amount: '' });
  const [revisionData, setRevisionData] = useState({ budget_id: '', revision_amount: '', reason: '' });
  const [invoiceData, setInvoiceData] = useState({ vendor_name: '', invoice_number: '', amount: '', tax_amount: '', budget_head: '' });

  // Slide-out State
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [progressData, setProgressData] = useState({ physical_progress_pct: 0, actual_amount: 0, status: 'Pending' });

  // Finance Slide-out State
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [budgetRevisions, setBudgetRevisions] = useState<BudgetRevision[]>([]);
  const [isRevisionsLoading, setIsRevisionsLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const fetchData = () => {
    setIsLoading(true);
    Promise.all([
      fetch(`/api/projects/${id}`).then(res => res.json()),
      fetch(`/api/projects/${id}/milestone-types`).then(res => res.json()),
      fetch(`/api/projects/${id}/clusters`).then(res => res.json()),
      fetch('/api/projects').then(res => res.json()),
      fetch(`/api/projects/${id}/additional-works`).then(res => res.json()),
      fetch(`/api/projects/${id}/budgets`).then(res => res.json()),
      fetch(`/api/projects/${id}/invoices`).then(res => res.json()),
      fetch('/api/vendors').then(res => res.json()).catch(() => [])
    ]).then(([data, typesData, clustersData, allProjData, worksData, budgetsData, invoicesData, vendorsData]) => {
        if (!data.error) {
          setProject(data.project);
          setMilestones(data.milestones);
          setInventory(data.inventory);
        }
        if (!typesData.error && Array.isArray(typesData)) {
          setMilestoneTypes(typesData);
        } else {
          setMilestoneTypes([]);
        }
        if (!clustersData.error) setClusters(clustersData);
        if (!allProjData.error) setAllProjects(allProjData);
        if (!worksData.error) setAdditionalWorks(worksData);
        if (!budgetsData.error) setBudgets(budgetsData);
        if (!invoicesData.error) setInvoices(invoicesData);
        if (Array.isArray(vendorsData)) setVendors(vendorsData);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleBudgetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/projects/${id}/budgets`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ budget_head: budgetData.budget_head, original_amount: parseFloat(budgetData.original_amount) })
    });
    setIsBudgetModalOpen(false);
    setBudgetData({ budget_head: '', original_amount: '' });
    fetchData();
  };

  const handleRevisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/projects/${id}/budgets/${revisionData.budget_id}/revise`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ revision_amount: parseFloat(revisionData.revision_amount), reason: revisionData.reason })
    });
    setIsRevisionModalOpen(false);
    setRevisionData({ budget_id: '', revision_amount: '', reason: '' });
    if (selectedBudget) fetchBudgetRevisions(selectedBudget.id);
    fetchData();
  };

  const handleInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/projects/${id}/invoices`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vendor_name: invoiceData.vendor_name,
        invoice_number: invoiceData.invoice_number,
        amount: parseFloat(invoiceData.amount),
        tax_amount: invoiceData.tax_amount ? parseFloat(invoiceData.tax_amount) : 0,
        budget_head: invoiceData.budget_head
      })
    });
    setIsInvoiceModalOpen(false);
    setInvoiceData({ vendor_name: '', invoice_number: '', amount: '', tax_amount: '', budget_head: '' });
    fetchData();
  };

  const handleMilestoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/projects/${id}/milestones`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...milestoneData,
        planned_amount: milestoneData.planned_amount ? parseFloat(milestoneData.planned_amount) : undefined,
        weightage_pct: milestoneData.weightage_pct ? parseFloat(milestoneData.weightage_pct) : undefined,
      })
    });
    setIsMilestoneModalOpen(false);
    setMilestoneData({ milestone_type_id: '', milestone_name: '', category: '', section: '', planned_amount: '', weightage_pct: '', planned_date: '', cluster_name: '', unit_number: '' });
    fetchData();
  };

  const handleInventorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/projects/${id}/inventory`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...inventoryData,
        sale_area_sqft: inventoryData.sale_area_sqft ? parseFloat(inventoryData.sale_area_sqft) : undefined,
        builtup_area_sqft: inventoryData.builtup_area_sqft ? parseFloat(inventoryData.builtup_area_sqft) : undefined,
      })
    });
    setIsInventoryModalOpen(false);
    setInventoryData({ cluster_name: '', unit_number: '', sale_area_sqft: '', builtup_area_sqft: '' });
    fetchData();
  };

  const handleTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTypeId) {
      await fetch(`/api/projects/${id}/milestone-types/${editingTypeId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_name: newTypeName })
      });
    } else {
      await fetch(`/api/projects/${selectedProjectId}/milestone-types`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type_name: newTypeName })
      });
    }
    setNewTypeName('');
    setEditingTypeId(null);
    fetchData(); 
  };

  const handleClusterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClusterName) return;
    await fetch(`/api/projects/${id}/clusters`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newClusterName })
    });
    setNewClusterName('');
    setIsClusterModalOpen(false);
    fetchData();
  };

  const handleWorkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workData.cluster_name || !workData.unit_number) {
      alert("Cluster and Unit Number are required for Additional Works.");
      return;
    }
    const url = editingWorkId 
      ? `/api/projects/${id}/additional-works/${editingWorkId}` 
      : `/api/projects/${id}/additional-works`;
    const method = editingWorkId ? 'PATCH' : 'POST';

    await fetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...workData,
        amount: parseFloat(workData.amount),
        gst_pct: parseFloat(workData.gst_pct),
        cess_pct: parseFloat(workData.cess_pct)
      })
    });
    setIsWorkModalOpen(false);
    setEditingWorkId(null);
    setWorkData({ item_name: '', description: '', amount: '', gst_pct: '18', cess_pct: '0', cluster_name: '', unit_number: '', notes: '' });
    fetchData();
  };

  const handleDeleteWork = async (workId: string) => {
    if (!window.confirm("Delete this work?")) return;
    await fetch(`/api/projects/${id}/additional-works/${workId}`, { method: 'DELETE' });
    fetchData();
  };

  const handleDeleteType = async (typeId: string) => {
    if(!window.confirm("Are you sure? This will also delete all milestones under this type.")) return;
    await fetch(`/api/projects/${id}/milestone-types/${typeId}`, { method: 'DELETE' });
    fetchData();
  };

  const handleProgressUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMilestone) return;

    await fetch(`/api/projects/milestones/${selectedMilestone.id}/progress`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        physical_progress_pct: Number(progressData.physical_progress_pct),
        actual_amount: Number(progressData.actual_amount),
        status: progressData.status
      })
    });
    setSelectedMilestone(null);
    fetchData();
  };

  const openBudget = async (b: Budget) => {
    setSelectedBudget(b);
    setBudgetRevisions([]);
    fetchBudgetRevisions(b.id);
  };

  const fetchBudgetRevisions = async (budgetId: string) => {
    setIsRevisionsLoading(true);
    try {
      const res = await fetch(`/api/projects/${id}/budgets/${budgetId}/revisions`);
      const data = await res.json();
      if (Array.isArray(data)) setBudgetRevisions(data);
      else if (data && Array.isArray(data.revisions)) setBudgetRevisions(data.revisions);
      else setBudgetRevisions([]);
    } catch {
      setBudgetRevisions([]);
    } finally {
      setIsRevisionsLoading(false);
    }
  };

  const updateInvoiceStatus = async (inv: Invoice, newStatus: string) => {
    setIsStatusUpdating(true);
    try {
      await fetch(`/api/projects/${id}/invoices/${inv.id}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      setSelectedInvoice({ ...inv, status: newStatus });
      fetchData();
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const openEditWork = (work: any) => {
    setEditingWorkId(work.id);
    setWorkData({
      item_name: work.item_name,
      description: work.description || '',
      amount: String(work.amount),
      gst_pct: String(work.gst_pct),
      cess_pct: String(work.cess_pct),
      cluster_name: work.cluster_name || '',
      unit_number: work.unit_number || '',
      notes: work.notes || ''
    });
    setIsWorkModalOpen(true);
  };

  if (isLoading) return <div className="p-10 text-center text-white">Loading Project...</div>;
  if (!project) return <div className="p-10 text-center text-red-500">Project Not Found</div>;

  const totalBudget = budgets.reduce((sum, b) => sum + Number(b.current_budget), 0);
  const totalInvoiced = invoices.reduce((sum, i) => sum + Number(i.total_amount), 0);
  const remainingBudget = totalBudget - totalInvoiced;

  const pendingTotal = invoices.filter(i => i.status === 'Pending').reduce((s, i) => s + Number(i.total_amount), 0);
  const approvedTotal = invoices.filter(i => i.status === 'Approved').reduce((s, i) => s + Number(i.total_amount), 0);
  const paidTotal = invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + Number(i.total_amount), 0);

  const invoicedByHead: Record<string, number> = {};
  invoices.forEach(i => {
    const head = i.budget_head || 'Unassigned';
    invoicedByHead[head] = (invoicedByHead[head] || 0) + Number(i.total_amount);
  });

  const chartHeads = budgets.map(b => b.budget_head);
  Object.keys(invoicedByHead).forEach(h => {
    if (!chartHeads.includes(h)) chartHeads.push(h);
  });

  const chartBudgetSeries = chartHeads.map(h => {
    const b = budgets.find(x => x.budget_head === h);
    return b ? Number(b.current_budget) : 0;
  });
  const chartInvoicedSeries = chartHeads.map(h => invoicedByHead[h] || 0);
  const chartRemainingSeries = chartHeads.map((h, i) => chartBudgetSeries[i] - chartInvoicedSeries[i]);

  return (
    <div className="space-y-6 max-w-[1600px] relative min-h-screen pt-4">
      
      <PageHelp 
        title="Project Command Center Overview" 
        description="Manage Milestones, Inventory, Additional Works, and Financials for this project."
      />

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/projects')} className="p-2 rounded-full hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ color: 'var(--text-secondary)' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{project.name}</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {project.company_name} | {project.location || 'No Location'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setIsClusterModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white border border-[var(--border-subtle)]" style={{ backgroundColor: 'var(--bg-panel)' }}>
            <Building size={18} /> Add Cluster
          </button>
          <button onClick={() => setIsTypeModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white border border-[var(--border-subtle)]" style={{ backgroundColor: 'var(--bg-panel)' }}>
            <Plus size={18} /> Add Type
          </button>
          <button onClick={() => setIsMilestoneModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white shadow-[0_0_15px_var(--brand-accent)]" style={{ backgroundColor: 'var(--brand-accent)' }}>
            <Plus size={18} /> Add Milestone
          </button>
          <button onClick={() => setIsInventoryModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white" style={{ backgroundColor: 'var(--bg-panel-alt)', border: '1px solid var(--border-subtle)' }}>
            <Plus size={18} /> Add Unit
          </button>
        </div>
      </div>

      {/* MACRO KPI STRIP */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Physical Progress</span><PieChart size={18} style={{ color: 'var(--brand-accent)' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">0.0%</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Revenue Collected</span><IndianRupee size={18} style={{ color: '#22C55E' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">₹0</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Construction Spent</span><IndianRupee size={18} style={{ color: '#F59E0B' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">₹0</div>
        </div>
        <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Delayed Milestones</span><AlertTriangle size={18} style={{ color: '#EF4444' }} /></div>
          <div className="text-3xl font-bold font-mono text-white">{milestones.filter(m => m.status === 'Delayed').length}</div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex items-center justify-between border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="flex">
          <button onClick={() => setActiveTab('milestones')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'milestones' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Milestones & Progress</button>
          <button onClick={() => setActiveTab('inventory')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'inventory' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Inventory ({inventory.length} Units)</button>
          <button onClick={() => setActiveTab('additional-works')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'additional-works' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Additional Works ({additionalWorks.length})</button>
          <button onClick={() => setActiveTab('finance')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'finance' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Finance</button>
        </div>
      </div>

      {/* TAB 1: MILESTONES */}
      {activeTab === 'milestones' && (
        <div className="rounded-xl overflow-hidden border min-h-[200px]" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Type</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Scope</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Milestone</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Progress</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Planned (₹)</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Actual (₹)</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map(m => (
                <tr key={m.id} onClick={() => { setSelectedMilestone(m); setProgressData({ physical_progress_pct: m.physical_progress_pct || 0, actual_amount: m.actual_amount || 0, status: m.status || 'Pending' }); }} className="border-t hover:bg-[var(--bg-panel-alt)] cursor-pointer transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-xs font-bold text-[var(--brand-accent)]">{m.milestone_type}</td>
                  <td className="p-4 text-xs text-gray-400">{m.cluster_name ? (m.unit_number ? `${m.cluster_name} - ${m.unit_number}` : m.cluster_name) : 'Project Level'}</td>
                  <td className="p-4 font-medium text-white">{m.milestone_name}</td>
                  <td className="p-4 text-center">{m.physical_progress_pct}%</td>
                  <td className="p-4 text-right font-mono text-sm text-gray-300">{(m.planned_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-right font-mono text-sm text-white">{(m.actual_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-center"><span className="px-2 py-1 rounded text-xs font-bold bg-[var(--bg-panel-alt)] text-white">{m.status}</span></td>
                </tr>
              ))}
              {milestones.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-gray-500">No milestones found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 2: INVENTORY */}
      {activeTab === 'inventory' && (
        <div className="rounded-xl overflow-hidden border min-h-[200px]" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Cluster</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Unit No.</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Sale Area (Sft)</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Sales Status</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map(inv => (
                <tr key={inv.id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-white font-medium">{inv.cluster_name}</td>
                  <td className="p-4 text-gray-300 font-mono">{inv.unit_number}</td>
                  <td className="p-4 text-right text-gray-300 font-mono">{inv.sale_area_sqft || '-'}</td>
                  <td className="p-4 text-center"><span className="px-2 py-1 rounded text-xs font-bold bg-green-500/20 text-green-500">{inv.sales_status}</span></td>
                </tr>
              ))}
              {inventory.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-500">No inventory found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 3: ADDITIONAL WORKS */}
      {activeTab === 'additional-works' && (
        <div className="rounded-xl overflow-hidden border min-h-[200px]" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <div className="p-4 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
            <div>
              <h3 className="text-lg font-bold text-white">Additional Works Catalogue</h3>
              <p className="text-xs text-gray-400 mt-1">Customer-requested upgrades per unit (Modular Kitchen, Flooring, etc.)</p>
            </div>
            <button onClick={() => { setEditingWorkId(null); setWorkData({ item_name: '', description: '', amount: '', gst_pct: '18', cess_pct: '0', cluster_name: '', unit_number: '', notes: '' }); setIsWorkModalOpen(true); }} className="px-4 py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>+ Add Work</button>
          </div>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Item</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Scope</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Amount</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Tax</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Notes</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {additionalWorks.map(w => (
                <tr key={w.id} className="border-t hover:bg-[var(--bg-panel-alt)]" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-white font-medium">{w.item_name}<div className="text-xs text-gray-400">{w.description}</div></td>
                  <td className="p-4 text-gray-300">{w.cluster_name ? `${w.cluster_name} - ${w.unit_number}` : 'All Units'}</td>
                  <td className="p-4 text-right font-mono text-gray-300">₹{Number(w.amount).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-center text-gray-400 text-xs">GST {w.gst_pct}%<br/>Other Taxes {w.cess_pct}%</td>
                  <td className="p-4 text-gray-400 text-xs">{w.notes || '-'}</td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => openEditWork(w)} className="p-1 text-blue-400 hover:text-blue-300"><Edit size={16} /></button>
                      <button onClick={() => handleDeleteWork(w.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {additionalWorks.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-500">No additional works defined.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 4: FINANCE */}
      {activeTab === 'finance' && (
        <div className="space-y-6">
          
          {/* FINANCE SUMMARY CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
              <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Total Budget (Sanctioned)</span><Banknote size={18} className="text-blue-400" /></div>
              <div className="text-3xl font-bold font-mono text-white">₹{(totalBudget / 10000000).toFixed(2)}Cr</div>
            </div>
            <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
              <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Total Invoiced (Spent)</span><FileText size={18} className="text-orange-400" /></div>
              <div className="text-3xl font-bold font-mono text-white">₹{(totalInvoiced / 10000000).toFixed(2)}Cr</div>
            </div>
            <div className="p-5 rounded-xl border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
              <div className="flex justify-between items-start mb-2"><span className="text-sm font-medium text-gray-400">Remaining Budget</span><TrendingUp size={18} className={remainingBudget >= 0 ? 'text-green-400' : 'text-red-400'} /></div>
              <div className={`text-3xl font-bold font-mono ${remainingBudget >= 0 ? 'text-white' : 'text-red-400'}`}>₹{(remainingBudget / 10000000).toFixed(2)}Cr</div>
            </div>
          </div>

          {/* BUDGET BY HEAD */}
          <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
            <div className="p-4 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
              <div>
                <h3 className="text-lg font-bold text-white">Budget by Head</h3>
                <p className="text-xs text-gray-400 mt-1">Original sanctioned budget vs revisions.</p>
              </div>
              <button onClick={() => setIsBudgetModalOpen(true)} className="px-4 py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>+ Add Budget Head</button>
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Budget Head</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Original Budget</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Revisions</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Current Budget</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {budgets.map(b => (
                  <tr key={b.id} onClick={() => openBudget(b)} className="border-t hover:bg-[var(--bg-panel-alt)] cursor-pointer transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="p-4 text-white font-medium flex items-center gap-2">{b.budget_head} <ChevronRight size={14} className="text-gray-500" /></td>
                    <td className="p-4 text-right font-mono text-gray-300">₹{Number(b.original_amount).toLocaleString('en-IN')}</td>
                    <td className="p-4 text-right font-mono text-blue-400">₹{Number(b.total_revised).toLocaleString('en-IN')}</td>
                    <td className="p-4 text-right font-mono text-white font-bold">₹{Number(b.current_budget).toLocaleString('en-IN')}</td>
                    <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => { setRevisionData({ budget_id: b.id, revision_amount: '', reason: '' }); setIsRevisionModalOpen(true); }} className="px-2 py-1 rounded text-xs font-bold bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">Revise</button>
                    </td>
                  </tr>
                ))}
                {budgets.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-500">No budgets defined yet.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* INVOICES */}
          <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
            <div className="p-4 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
              <div>
                <h3 className="text-lg font-bold text-white">Vendor Invoices</h3>
                <p className="text-xs text-gray-400 mt-1">Click any row to view detail and change status (Approve / Mark Paid).</p>
              </div>
              <button onClick={() => setIsInvoiceModalOpen(true)} className="px-4 py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>+ Add Invoice</button>
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Date</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Vendor</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Inv No</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Budget Head</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Total Amount</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i.id} onClick={() => setSelectedInvoice(i)} className="border-t hover:bg-[var(--bg-panel-alt)] cursor-pointer transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="p-4 text-xs text-gray-300">{new Date(i.invoice_date).toLocaleDateString()}</td>
                    <td className="p-4 text-white font-medium">{i.vendor_name}</td>
                    <td className="p-4 text-gray-400 font-mono text-xs">{i.invoice_number}</td>
                    <td className="p-4 text-gray-300 text-xs">{i.budget_head || '-'}</td>
                    <td className="p-4 text-right font-mono text-white">₹{Number(i.total_amount).toLocaleString('en-IN')}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        i.status === 'Paid' ? 'bg-green-500/20 text-green-400' : 
                        i.status === 'Approved' ? 'bg-blue-500/20 text-blue-400' : 
                        'bg-orange-500/20 text-orange-400'
                      }`}>{i.status}</span>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-500">No invoices logged yet.</td></tr>}
              </tbody>
            </table>
          </div>

        </div>
      )}

      {/* MILESTONE MODAL */}
      {isMilestoneModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsMilestoneModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Add Milestone</h2>
              <button onClick={() => setIsMilestoneModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleMilestoneSubmit} className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Milestone Name</label>
                <input required type="text" value={milestoneData.milestone_name} onChange={e => setMilestoneData({...milestoneData, milestone_name: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                <select 
                  required={!milestoneData.milestone_type_id} 
                  value={milestoneData.milestone_type_id} 
                  onChange={e => {
                    if (e.target.value === 'NEW_TYPE') {
                      setNewTypeName('');
                      setMilestoneData({...milestoneData, milestone_type_id: 'NEW_TYPE'});
                    } else {
                      setMilestoneData({...milestoneData, milestone_type_id: e.target.value});
                    }
                  }} 
                  className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white"
                >
                  <option value="">Select Type</option>
                  {(Array.isArray(milestoneTypes) ? milestoneTypes : (milestoneTypes as any)?.types || []).map((t: any) => (
                    <option key={t.id} value={t.id}>{t.type_name}</option>
                  ))}
                  <option value="NEW_TYPE" className="text-[var(--brand-accent)] font-bold">+ Add New Milestone Type</option>
                </select>

                {milestoneData.milestone_type_id === 'NEW_TYPE' && (
                  <div className="flex gap-2 mt-2">
                    <input 
                      required 
                      type="text" 
                      value={newTypeName} 
                      onChange={e => setNewTypeName(e.target.value)} 
                      className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" 
                      placeholder="Enter new type name (e.g. Civil)" 
                    />
                    <button 
                      type="button" 
                      onClick={async (e) => {
                        e.preventDefault();
                        if (!newTypeName) return;
                        const res = await fetch(`/api/projects/${selectedProjectId}/milestone-types`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ type_name: newTypeName })
                        });
                        const data = await res.json();
                        setNewTypeName('');
                        await fetchData(); 
                        if (data && data.id) {
                          setMilestoneData({...milestoneData, milestone_type_id: data.id});
                        }
                      }}
                      className="px-3 py-2 rounded text-white font-bold" 
                      style={{ backgroundColor: 'var(--brand-accent)' }}
                    >
                      Save
                    </button>
                    <button type="button" onClick={() => setMilestoneData({...milestoneData, milestone_type_id: ''})} className="px-3 py-2 rounded text-gray-400 border border-[var(--border-subtle)]">
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                  <input type="text" value={milestoneData.category} onChange={e => setMilestoneData({...milestoneData, category: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="e.g. Civil" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Section</label>
                  <input type="text" value={milestoneData.section} onChange={e => setMilestoneData({...milestoneData, section: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="e.g. Block A" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Cluster</label>
                  <select value={milestoneData.cluster_name} onChange={e => setMilestoneData({...milestoneData, cluster_name: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                    <option value="">Project Level (No Cluster)</option>
                    {clusters.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Unit Number</label>
                  <select value={milestoneData.unit_number} onChange={e => setMilestoneData({...milestoneData, unit_number: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                    <option value="">N/A</option>
                    {inventory
                      .filter(inv => !milestoneData.cluster_name || inv.cluster_name === milestoneData.cluster_name)
                      .map(inv => <option key={inv.id} value={inv.unit_number}>{inv.unit_number}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Planned (₹)</label>
                  <input type="number" value={milestoneData.planned_amount} onChange={e => setMilestoneData({...milestoneData, planned_amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Weightage (%)</label>
                  <input type="number" value={milestoneData.weightage_pct} onChange={e => setMilestoneData({...milestoneData, weightage_pct: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Planned Date</label>
                  <input type="date" value={milestoneData.planned_date} onChange={e => setMilestoneData({...milestoneData, planned_date: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" style={{ colorScheme: 'dark' }} />
                </div>
              </div>
              
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Milestone</button>
            </form>
          </div>
        </div>
      )}

      {/* TYPE MODAL */}
      {isTypeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsTypeModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Add Milestone Type</h2>
              <button onClick={() => setIsTypeModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleTypeSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Type Name</label>
                <input required type="text" value={newTypeName} onChange={e => setNewTypeName(e.target.value)} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Type</button>
            </form>
          </div>
        </div>
      )}

      {/* CLUSTER MODAL */}
      {isClusterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsClusterModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Add Cluster</h2>
              <button onClick={() => setIsClusterModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleClusterSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Cluster Name</label>
                <input required type="text" value={newClusterName} onChange={e => setNewClusterName(e.target.value)} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>

              {/* EXISTING CLUSTERS LIST */}
	      <div className="mt-4 pt-4 border-t border-[var(--border-subtle)]">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Existing Clusters</label>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(clusters) ? clusters : []).map(c => (
                    <span key={c} className="px-2 py-1 bg-[var(--bg-panel-alt)] border border-[var(--border-subtle)] text-xs text-gray-300 rounded">
                      {c}
                    </span>
                  ))}
                  {(!Array.isArray(clusters) || clusters.length === 0) && (
                    <span className="text-xs text-gray-500 italic">No clusters added yet.</span>
                  )}
                </div>
              </div>

              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Cluster</button>
            </form>
          </div>
        </div>
      )}

      {/* INVENTORY / UNIT MODAL */}
      {isInventoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsInventoryModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">Add Unit</h2>
              <button onClick={() => setIsInventoryModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleInventorySubmit} className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Cluster</label>
                <select required value={inventoryData.cluster_name} onChange={e => setInventoryData({...inventoryData, cluster_name: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                  <option value="">Select Cluster</option>
                  {clusters.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Unit Number</label>
                <input required type="text" value={inventoryData.unit_number} onChange={e => setInventoryData({...inventoryData, unit_number: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Sale Area (Sft)</label>
                  <input type="number" value={inventoryData.sale_area_sqft} onChange={e => setInventoryData({...inventoryData, sale_area_sqft: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Builtup Area (Sft)</label>
                  <input type="number" value={inventoryData.builtup_area_sqft} onChange={e => setInventoryData({...inventoryData, builtup_area_sqft: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Unit</button>
            </form>
          </div>
        </div>
      )}

      {/* ADDITIONAL WORK MODAL */}
      {isWorkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsWorkModalOpen(false)}></div>
          <div className="w-full max-w-md bg-[var(--bg-panel)] shadow-2xl border border-[var(--border-subtle)] rounded-xl flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)] rounded-t-xl">
              <h2 className="text-xl font-bold text-white">{editingWorkId ? 'Edit Additional Work' : 'Add Additional Work'}</h2>
              <button onClick={() => setIsWorkModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
                        <form onSubmit={handleWorkSubmit} className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Item Name</label>
                <input required type="text" value={workData.item_name} onChange={e => setWorkData({...workData, item_name: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Cluster</label>
                  <select required value={workData.cluster_name} onChange={e => setWorkData({...workData, cluster_name: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                    <option value="">Select Cluster</option>
                    {clusters.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Unit Number</label>
                  <select required value={workData.unit_number} onChange={e => setWorkData({...workData, unit_number: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                    <option value="">Select Unit</option>
                    {inventory
                      .filter(inv => !workData.cluster_name || inv.cluster_name === workData.cluster_name)
                      .map(inv => <option key={inv.id} value={inv.unit_number}>{inv.unit_number}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <input type="text" value={workData.description} onChange={e => setWorkData({...workData, description: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Amount (₹)</label>
                  <input required type="number" value={workData.amount} onChange={e => setWorkData({...workData, amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">GST (%)</label>
                  <input type="number" value={workData.gst_pct} onChange={e => setWorkData({...workData, gst_pct: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Cess (%)</label>
                  <input type="number" value={workData.cess_pct} onChange={e => setWorkData({...workData, cess_pct: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
                </div>
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Work</button>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* FINANCE SLIDE-OUTS & MODALS                               */}
      {/* ========================================================= */}

      {/* BUDGET SLIDE-OUT */}
      {isBudgetModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsBudgetModalOpen(false)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)]">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">Add Sanctioned Budget <HelpTooltip text="Baseline budget before revisions." /></h2>
              <button onClick={() => setIsBudgetModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleBudgetSubmit} className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Budget Head <HelpTooltip text="E.g., Construction, Marketing, HR, Admin" /></label>
                <input required type="text" value={budgetData.budget_head} onChange={e => setBudgetData({...budgetData, budget_head: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="Construction" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Original Amount (₹) <HelpTooltip text="The sanctioned baseline. Revisions will be tracked separately against this." /></label>
                <input required type="number" value={budgetData.original_amount} onChange={e => setBudgetData({...budgetData, original_amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" placeholder="50000000" />
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Budget</button>
            </form>
          </div>
        </div>
      )}

      {/* REVISION SLIDE-OUT */}
      {isRevisionModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsRevisionModalOpen(false)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)]">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">Revise Budget <HelpTooltip text="Adjust budget for price escalation or scope changes." /></h2>
              <button onClick={() => setIsRevisionModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleRevisionSubmit} className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Revision Amount (₹) <HelpTooltip text="Use positive for increase, negative for decrease." /></label>
                <input required type="number" value={revisionData.revision_amount} onChange={e => setRevisionData({...revisionData, revision_amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" placeholder="5000000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Reason for Revision <HelpTooltip text="Captured in audit trail. E.g. steel escalation, scope increase." /></label>
                <textarea required rows={3} value={revisionData.reason} onChange={e => setRevisionData({...revisionData, reason: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="Abnormal price escalation of steel..." />
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Apply Revision</button>
            </form>
          </div>
        </div>
      )}

      {/* INVOICE SLIDE-OUT (Add) */}
      {isInvoiceModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsInvoiceModalOpen(false)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)]">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">Log Invoice <HelpTooltip text="Record a vendor bill against the project budget." /></h2>
              <button onClick={() => setIsInvoiceModalOpen(false)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleInvoiceSubmit} className="p-6 flex-1 overflow-y-auto space-y-4">
              
              <div className="relative">
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Vendor <HelpTooltip text="Search and select the vendor from your master list." /></label>
                
                <div 
                  onClick={() => setIsVendorDropdownOpen(!isVendorDropdownOpen)}
                  className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white cursor-pointer flex justify-between items-center"
                >
                  <span className={invoiceData.vendor_name ? 'text-white' : 'text-gray-500'}>
                    {invoiceData.vendor_name || 'Search and select vendor...'}
                  </span>
                  <ChevronRight size={16} className={`transition-transform ${isVendorDropdownOpen ? 'rotate-90' : ''}`} />
                </div>

                {isVendorDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-[var(--bg-panel)] border border-[var(--border-subtle)] rounded shadow-xl max-h-64 flex flex-col">
                    <div className="p-2 border-b border-[var(--border-subtle)] space-y-2 bg-[var(--bg-panel-alt)]">
                      <input 
                        type="text" 
                        placeholder="Search vendor name..." 
                        value={vendorSearch}
                        onChange={(e) => setVendorSearch(e.target.value)}
                        className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-1.5 text-sm text-white focus:outline-none focus:border-[var(--brand-accent)]"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <select
                        value={vendorTypeFilter}
                        onChange={(e) => setVendorTypeFilter(e.target.value)}
                        className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-1.5 text-sm text-white focus:outline-none"
                      >
                        <option value="All">All Types</option>
                        {Array.from(new Set(vendors.map(v => v.vendor_type))).map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div className="overflow-y-auto flex-1 p-1">
                      {(() => {
                        const filtered = vendors.filter(v => {
                          const matchSearch = v.name.toLowerCase().includes(vendorSearch.toLowerCase());
                          const matchType = vendorTypeFilter === 'All' || v.vendor_type === vendorTypeFilter;
                          return matchSearch && matchType;
                        });

                        if (filtered.length === 0) {
                          return <div className="p-3 text-sm text-gray-500 text-center">No vendors found.</div>;
                        }

                        return filtered.map(v => (
                          <div 
                            key={v.id}
                            onClick={() => {
                              setInvoiceData({...invoiceData, vendor_name: v.name});
                              setIsVendorDropdownOpen(false);
                              setVendorSearch('');
                            }}
                            className="p-2 text-sm text-gray-300 hover:bg-[var(--bg-panel-alt)] hover:text-white cursor-pointer rounded flex justify-between items-center"
                          >
                            <span>{v.name}</span>
                            <span className="text-xs text-gray-500">{v.vendor_type}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Invoice Number <HelpTooltip text="Must be unique per vendor for reconciliation." /></label>
                <input required type="text" value={invoiceData.invoice_number} onChange={e => setInvoiceData({...invoiceData, invoice_number: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" placeholder="INV-2026-001" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Amount (₹) <HelpTooltip text="Pre-tax amount." /></label>
                  <input required type="number" value={invoiceData.amount} onChange={e => setInvoiceData({...invoiceData, amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" placeholder="100000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Tax Amount (₹) <HelpTooltip text="GST or other applicable taxes." /></label>
                  <input type="number" value={invoiceData.tax_amount} onChange={e => setInvoiceData({...invoiceData, tax_amount: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" placeholder="18000" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Budget Head <HelpTooltip text="Must match an existing Budget Head above." /></label>
                <select required value={invoiceData.budget_head} onChange={e => setInvoiceData({...invoiceData, budget_head: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                  <option value="">Select Budget Head</option>
                  {budgets.map(b => <option key={b.id} value={b.budget_head}>{b.budget_head}</option>)}
                </select>
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Invoice</button>
            </form>
          </div>
        </div>
      )}

      {/* BUDGET DETAIL SLIDE-OUT (row click) */}
      {selectedBudget && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setSelectedBudget(null)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-start bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--brand-accent)]">Budget Head Detail</span>
                <h2 className="text-xl font-bold text-white mt-1">{selectedBudget.budget_head}</h2>
              </div>
              <button onClick={() => setSelectedBudget(null)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                  <div className="text-xs text-gray-400">Original</div>
                  <div className="text-lg font-mono font-bold text-white">₹{Number(selectedBudget.original_amount).toLocaleString('en-IN')}</div>
                </div>
                <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                  <div className="text-xs text-gray-400">Revisions</div>
                  <div className="text-lg font-mono font-bold text-blue-400">₹{Number(selectedBudget.total_revised).toLocaleString('en-IN')}</div>
                </div>
                <div className="col-span-2 p-3 rounded-lg border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                  <div className="text-xs text-gray-400">Current Budget</div>
                  <div className="text-xl font-mono font-bold text-white">₹{Number(selectedBudget.current_budget).toLocaleString('en-IN')}</div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
                  Revision History
                  <HelpTooltip text="Full audit trail of every budget change with reason." />
                </h3>
                {isRevisionsLoading && <div className="text-gray-500 text-sm">Loading revisions...</div>}
                {!isRevisionsLoading && budgetRevisions.length === 0 && (
                  <div className="text-gray-500 text-sm p-3 rounded border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                    No revisions yet. This is the original sanctioned amount.
                  </div>
                )}
                <div className="space-y-2">
                  {budgetRevisions.map(r => (
                    <div key={r.id} className="p-3 rounded border" style={{ backgroundColor: 'var(--bg-panel-alt)', borderColor: 'var(--border-subtle)' }}>
                      <div className="flex justify-between items-center">
                        <span className={`font-mono text-sm font-bold ${Number(r.revision_amount) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {Number(r.revision_amount) >= 0 ? '+' : ''}₹{Number(r.revision_amount).toLocaleString('en-IN')}
                        </span>
                        {r.created_at && <span className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{r.reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => { setRevisionData({ budget_id: selectedBudget.id, revision_amount: '', reason: '' }); setIsRevisionModalOpen(true); }}
                className="w-full py-2 rounded text-white font-bold"
                style={{ backgroundColor: 'var(--brand-accent)' }}
              >
                + Add Revision
              </button>
            </div>
          </div>
        </div>
      )}

      {/* INVOICE DETAIL SLIDE-OUT (row click) */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setSelectedInvoice(null)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-start bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--brand-accent)]">Invoice Detail</span>
                <h2 className="text-xl font-bold text-white mt-1">{selectedInvoice.invoice_number}</h2>
                <p className="text-sm text-gray-400 mt-1">{selectedInvoice.vendor_name}</p>
              </div>
              <button onClick={() => setSelectedInvoice(null)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-400">Date</span><span className="font-mono text-gray-300">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">Budget Head</span><span className="text-gray-300">{selectedInvoice.budget_head || '-'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">Amount</span><span className="font-mono text-gray-300">₹{Number(selectedInvoice.amount).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">Tax</span><span className="font-mono text-gray-300">₹{Number(selectedInvoice.tax_amount).toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <span className="text-gray-400">Total</span>
                  <span className="font-mono text-white font-bold">₹{Number(selectedInvoice.total_amount).toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Current Status</span>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    selectedInvoice.status === 'Paid' ? 'bg-green-500/20 text-green-400' :
                    selectedInvoice.status === 'Approved' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-orange-500/20 text-orange-400'
                  }`}>{selectedInvoice.status}</span>
                </div>
              </div>

              <div className="pt-4 space-y-2">
                {selectedInvoice.status === 'Pending' && (
                  <button disabled={isStatusUpdating} onClick={() => updateInvoiceStatus(selectedInvoice, 'Approved')} className="w-full py-2 rounded text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50" style={{ backgroundColor: 'var(--brand-accent)' }}>
                    <CheckCircle size={16} /> Approve Invoice
                  </button>
                )}
                {selectedInvoice.status === 'Approved' && (
                  <button disabled={isStatusUpdating} onClick={() => updateInvoiceStatus(selectedInvoice, 'Paid')} className="w-full py-2 rounded text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50" style={{ backgroundColor: 'var(--brand-accent)' }}>
                    <Banknote size={16} /> Mark as Paid
                  </button>
                )}
                {selectedInvoice.status === 'Paid' && (
                  <div className="w-full py-2 rounded text-center text-sm text-green-400 font-bold border border-green-500/30 bg-green-500/10">
                    ✓ Fully Settled
                  </div>
                )}
                {selectedInvoice.status !== 'Pending' && (
                  <button disabled={isStatusUpdating} onClick={() => updateInvoiceStatus(selectedInvoice, 'Pending')} className="w-full py-2 rounded text-xs text-gray-400 hover:text-white border border-[var(--border-subtle)] disabled:opacity-50 flex items-center justify-center gap-1">
                    <Clock size={12} /> Revert to Pending
                  </button>
                )}
              </div>

              <div className="pt-4">
                <p className="text-xs text-gray-500 flex items-start gap-2">
                  <HelpTooltip text="Status changes are auditable. Only Pending → Approved → Paid is the sanctioned path." />
                  <span>Approval is required before an invoice counts against the budget.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- EXISTING MILESTONE PROGRESS SLIDE-OUT --- */}
      {selectedMilestone && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setSelectedMilestone(null)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-center bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--brand-accent)]">Update Progress</span>
                <h2 className="text-xl font-bold text-white mt-1">{selectedMilestone.milestone_name}</h2>
              </div>
              <button onClick={() => setSelectedMilestone(null)} className="p-1 text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleProgressUpdate} className="p-6 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Physical Progress (%) <HelpTooltip text="0-100. Represents actual physical work completion." /></label>
                <input required type="number" min="0" max="100" step="0.1" value={progressData.physical_progress_pct} onChange={e => setProgressData({...progressData, physical_progress_pct: Number(e.target.value)})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Actual Amount Spent (₹) <HelpTooltip text="Cumulative cost booked against this milestone." /></label>
                <input required type="number" value={progressData.actual_amount} onChange={e => setProgressData({...progressData, actual_amount: Number(e.target.value)})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">Status <HelpTooltip text="Pending, In Progress, Completed, or Delayed." /></label>
                <select value={progressData.status} onChange={e => setProgressData({...progressData, status: e.target.value})} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white">
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Delayed">Delayed</option>
                </select>
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold mt-4" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Progress</button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
